import { FormProvider, useForm } from 'react-hook-form';
import { type FC, Fragment, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Save } from 'lucide-react';
import { z } from 'zod';
import { type RoleKey, TenantUserRole, tenantUserRoleOptions } from '@/types/tenantUserRole';
import { renderField } from '@/components/inspector/entity-fields/form/field-components';
import { type BackendError, useFormErrorHandler } from '@/hooks/useFormErrorHandler';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { type RowEntity } from '@/hooks/data/datatable/useTableStore';
import { ToastHighlight } from '@/components/ui/toast-highlight';
import { error as errorLog, warn } from '@/utilities/log';
import { Separator } from '@/components/ui/separator';
import { updateUser } from '@/services/users.service';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { useUser } from '@/hooks/users/useUsers';
import { Button } from '@/components/ui/button';
import { QueryKey } from '@/lib/queryKeys';
import { type User } from '@/types/users';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const UserFields: FC<{ user: RowEntity; enabled: boolean }> = ({ user: userProp, enabled }) => {
  const queryClient = useQueryClient();
  const tErrors = useTranslations('errors');
  const tCommon = useTranslations('common');
  const tToasts = useTranslations('toasts');
  const tValidation = useTranslations('validation');
  const tRoles = useTranslations('users.roles');

  const { user_id } = userProp.original as User;
  const { toast } = useToast();
  const { user, userIsFetching, userIsPending } = useUser(user_id, {
    enabled: enabled,
  });

  const editUserWithAuth = useAuthenticatedQueryFn(updateUser);

  const editUserMutation = useMutation({
    mutationFn: editUserWithAuth,
    onSuccess: (result) => {
      toast({
        title: tToasts('editUserMutation.title'),
        descriptionClassName: 'flex min-w-0 whitespace-pre',
        description: tToasts.rich('editUserMutation.description', {
          userName: result.name,
          role: tRoles(result.role as RoleKey),
          highlight: (chunks) => <ToastHighlight chunks={chunks} />,
        }),
      });

      void queryClient.invalidateQueries({ queryKey: [QueryKey.UserList] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.User, user_id] });
    },
    onError: (err: BackendError) => handleError(err),
  });

  const isLoading = editUserMutation.isPending;
  const isCollaborator = user.role === TenantUserRole.Collaborator;

  const formFields: Array<any> = useMemo(
    () => [
      {
        'name': 'Name',
        'slug': 'name',
        'type': 'string',
        'disabled': true,
        'value': user.name ?? '',
        'data-cy': 'user-field-name',
        'isLoading': userIsFetching,
      },
      {
        'name': 'Email',
        'slug': 'email',
        'type': 'string',
        'disabled': true,
        'hidden': !user.profile?.email,
        'value': user.profile?.email ?? '',
        'data-cy': 'user-email-field',
        'isLoading': userIsFetching,
      },
      isCollaborator
        ? {
            'name': 'Role',
            'slug': 'role',
            'type': 'string',
            'disabled': true,
            'value': tRoles('guest'),
            'data-cy': 'user-role-field',
            'isLoading': userIsFetching,
          }
        : {
            'name': 'Role',
            'slug': 'role',
            'type': 'select',
            'options': tenantUserRoleOptions.map((option) => ({
              ...option,
              label: tRoles(option.value as RoleKey),
            })),
            'disabled': userIsFetching || userIsPending || isLoading,
            'placeholder': user.role ? tRoles(user.role as RoleKey) : '',
            'validation': z.enum(Object.values(TenantUserRole) as [string, ...Array<string>], {
              error: (issue) => {
                if (!issue.input) {
                  return tValidation('fieldRequired', { field: tValidation('fields.role') });
                }
              },
            }),
            'value': user.role ?? '',
            'data-cy': 'user-role-field',
            'isLoading': userIsFetching,
          },
    ],
    [user, userIsFetching, userIsPending, isLoading, isCollaborator, tValidation, tRoles],
  );

  const formSchema = z.object(
    formFields.reduce(
      (acc, field) => {
        if (field.validation) {
          // Include only fields with validation property in the form schema
          acc[field.slug] = field.validation;
        }
        return acc;
      },
      {} as Record<string, z.ZodType<any>>,
    ),
  );

  const defaultValues = formFields.reduce(
    (acc, field) => {
      acc[field.slug] = field.value;
      return acc;
    },
    {} as Record<string, any>,
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
    values: defaultValues,
  });

  const { handleError } = useFormErrorHandler(form.setError, form.getValues);

  function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      if (typeof values === 'object' && values !== null) {
        editUserMutation.mutate({
          id: user.user_id,
          body: {
            role: values.role,
          },
        });
      } else {
        warn('Invalid data for editing user');
      }
    } catch (err) {
      errorLog('Action failed:', err);
      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: tErrors('toastDefaultDescription'),
      });
    }
  }

  return (
    <div className="p-3">
      {userIsPending ? (
        <div className="relative flex flex-col space-y-6">
          {formFields.map((field, index) => (
            <Fragment key={`skeleton-${field.slug}`}>
              <div className="flex flex-col space-y-3">
                {field.name && <Skeleton className={cn('h-5 w-1/3', { 'w-2/5': index % 2 })} />}
                {['text', 'string', 'float', 'integer', 'select', 'date'].includes(field.type) && (
                  <Skeleton className="h-8 w-full" />
                )}
                {field.description && <Skeleton className={cn('h-5 w-1/3', { 'w-2/3': index % 2 })} />}
              </div>
              <Separator className="block last:hidden" />
            </Fragment>
          ))}
        </div>
      ) : (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative flex flex-col space-y-6">
            {formFields.map((field) => renderField(form, field))}

            {!isCollaborator && (
              <Button
                type="submit"
                variant="navigation-action-primary"
                className="w-full justify-center focus-visible:ring-offset-ui-panel"
                disabled={userIsFetching || isLoading || !form.formState.isDirty}
                data-cy="user-save-button"
                iconClassName={cn('icon-4!', { 'animate-spin': isLoading })}
                icon={isLoading ? Loader2 : Save}
              >
                {isLoading ? tCommon('saving') : tCommon('save')}
              </Button>
            )}
          </form>
        </FormProvider>
      )}
    </div>
  );
};
